import sparklesAnimation from '@assets/lottie/sparkles.json'
import { AnimationPlayer } from '@components/AnimationPlayer'
import Touchable from '@components/Touchable'
import useLlmChat from '@plug-core/hooks/useLlmChat'
import { type MessageWithStream } from '@plug-core/utils/llmChat'
import { Button, Input, Spinner, cn } from '@ui'
import DOMPurify from 'dompurify'
import { motion } from 'framer-motion'
import { marked } from 'marked'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IoSend } from 'react-icons/io5'
import classes from './chatProse.module.css'

const ChatMessage = ({
  message,
  updateMessageContent,
  markAsDone,
}: {
  message: MessageWithStream
  updateMessageContent: (content: string) => void
  markAsDone: () => void
}) => {
  const messageContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const getStream = async () => {
      if (message.stream) {
        const decoder = new TextDecoder()
        const { body } = await message.stream

        for await (const part of body) {
          if (typeof part === 'string') {
            updateMessageContent(part)
          } else {
            updateMessageContent(decoder.decode(part))
          }
        }

        markAsDone()
      }
    }

    getStream()
  }, [message.stream])

  useEffect(() => {
    if (!messageContentRef.current) return

    messageContentRef.current.addEventListener('click', (e) => {
      const potentialLink = e.target as HTMLLinkElement

      if (potentialLink?.nodeName === 'A') {
        e.preventDefault()
        window.open(potentialLink.href, '_blank')
      }
    })
  }, [messageContentRef])

  const content = DOMPurify.sanitize(marked.parse(message.content) as string)

  const isUser = message.role === 'user'

  return (
    <div
      className={cn(
        'my-2 max-w-[75%] rounded-md px-3',
        isUser ? 'ml-auto bg-accent text-on-accent' : 'mr-auto bg-surface text-fg',
        message.role !== 'user' && message.role !== 'assistant' && 'mx-auto'
      )}
    >
      <div
        className={cn(classes.prose, isUser && classes.onAccent)}
        dangerouslySetInnerHTML={content ? { __html: content } : undefined}
        ref={messageContentRef}
      >
        {content ? undefined : <PendingMessageContent />}
      </div>
    </div>
  )
}

const PendingMessageContent = () => {
  const [highlightIndex, setHighlightIndex] = useState(0)

  useEffect(() => {
    setInterval(() => {
      setHighlightIndex((i) => {
        if (i === 4) {
          return 0
        } else {
          return i + 1
        }
      })
    }, 300)
  }, [])

  const getStyle = (idx: number) => {
    if (highlightIndex === idx) {
      return { color: 'var(--plug-text)', fontSize: '24px' }
    } else {
      return { color: 'var(--plug-text-2)', fontSize: '18px' }
    }
  }

  return (
    <div className="flex w-6 flex-row py-3">
      <motion.span animate={getStyle(0)}>•</motion.span>
      <motion.span animate={getStyle(1)}>•</motion.span>
      <motion.span animate={getStyle(2)}>•</motion.span>
    </div>
  )
}

export default () => {
  const {
    messages,
    question,
    setQuestion,
    submitQuestion,
    bubbles,
    isWriting,
    disabled,
    updateMessage,
  } = useLlmChat()

  const { t } = useTranslation()
  const scrollView = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollView.current!.scrollTo(0, scrollView.current!.scrollHeight)
  }, [messages.length])

  return (
    <div className="mb-3 flex h-[calc(100%-80px)] flex-col">
      <div className="flex h-full flex-col-reverse overflow-scroll" ref={scrollView}>
        {[...messages].reverse().map((msg) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            updateMessageContent={(content) =>
              updateMessage(msg.id, (m) => ({ content: m.content + content }))
            }
            markAsDone={() => updateMessage(msg.id, { doneStreaming: true })}
          />
        ))}
      </div>

      {messages.length < 2 && !isWriting && (
        <div className="mt-2 flex flex-col items-end gap-1.5">
          {bubbles.map((bubble) => (
            <Button
              variant="secondary"
              size="sm"
              className="font-normal"
              key={bubble.message}
              onClick={() => submitQuestion(bubble.message)}
            >
              {bubble.message} {bubble.emoji}
            </Button>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-row items-center">
        <div className="grow">
          <Input
            placeholder={t('llmChat.inputPlaceholder')}
            aria-label={t('llmChat.inputPlaceholder')}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitQuestion(question)}
            autoFocus
          />
        </div>

        <Touchable
          style={{
            position: 'relative',
            marginLeft: 12,
            borderRadius: 999,
            filter: disabled ? 'brightness(0.7)' : undefined,
            cursor: isWriting ? 'progress' : disabled ? 'default' : 'pointer',
          }}
          scaleFactor={0.1}
          onClick={() => submitQuestion(question)}
          disabled={disabled}
        >
          <div className="rounded-full bg-[image:var(--plug-grad-mark)] p-[3px] opacity-90">
            <div className="flex size-[43px] items-center justify-center rounded-full bg-bg text-fg">
              {isWriting ? <Spinner size={20} /> : <IoSend size={20} aria-hidden="true" />}

              {!disabled && (
                <div className="absolute top-[-11px] left-[-29px] w-[100px] opacity-75">
                  <AnimationPlayer animationData={sparklesAnimation} />
                </div>
              )}
            </div>
          </div>
        </Touchable>
      </div>
    </div>
  )
}
